import React from "react";
import { formatNumber } from "@/lib/utils";
import { useCompanyConfig } from "@/hooks/useCompanyConfig";

/** Ngày dd/MM/yyyy từ chuỗi yyyy-MM-dd (không qua Date để không lệch múi giờ) */
function vnDate(value?: string | null): string {
  if (!value) return "…";
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : String(value);
}

function ReportHeader({ title, subtitle }: { title: string; subtitle?: React.ReactNode }) {
  const config = useCompanyConfig(true);
  return (
    <>
      <div className="flex items-start justify-between border-b pb-3 mb-4 border-slate-800">
        <div className="flex-1 pr-4">
          <h2 className="text-base font-bold uppercase">{config?.companyName || "CÔNG TY"}</h2>
          {config?.address1 && <p>Địa chỉ: {config.address1}</p>}
          {config?.taxCode && <p>Mã số thuế: {config.taxCode}</p>}
        </div>
        {config?.logoUrl && (
          <img src={config.logoUrl} alt="Logo" className="max-h-[56px] max-w-[120px] object-contain" />
        )}
      </div>
      <div className="text-center mb-4">
        <h1 className="text-xl font-bold uppercase tracking-wide">{title}</h1>
        {subtitle && <div className="italic mt-1">{subtitle}</div>}
      </div>
    </>
  );
}

function Signatures() {
  return (
    <div className="grid grid-cols-3 text-center mt-8 break-inside-avoid">
      {["Người lập biểu", "Thủ kho", "Kế toán trưởng"].map((role) => (
        <div key={role}>
          <div className="font-bold">{role}</div>
          <div className="italic text-[11px] mb-14">(Ký, họ tên)</div>
        </div>
      ))}
    </div>
  );
}

const th = "border border-slate-800 px-1.5 py-1 font-semibold";
const td = "border border-slate-800 px-1.5 py-1";

export interface NxtPrintRow {
  itemCode?: string;
  itemName?: string;
  unit?: string;
  warehouseName?: string;
  lotNumber?: string;
  opening: number;
  inQty: number;
  outQty: number;
  closing: number;
}

/** Mẫu in / PDF Báo cáo Nhập – Xuất – Tồn của một kho / nhóm kho */
export function InventoryNxtPrintTemplate({
  title,
  warehouseLabel,
  startDate,
  endDate,
  rows,
}: {
  title: string;
  warehouseLabel: string;
  startDate?: string;
  endDate?: string;
  rows: NxtPrintRow[];
}) {
  const total = rows.reduce(
    (s, r) => ({ opening: s.opening + r.opening, inQty: s.inQty + r.inQty, outQty: s.outQty + r.outQty, closing: s.closing + r.closing }),
    { opening: 0, inQty: 0, outQty: 0, closing: 0 },
  );
  return (
    <div className="text-[12px] leading-snug text-slate-900 font-serif bg-white" data-testid="nxt-print-template">
      <ReportHeader
        title={title}
        subtitle={<>{warehouseLabel} — Từ ngày {vnDate(startDate)} đến ngày {vnDate(endDate)}</>}
      />
      <table className="w-full border-collapse border border-slate-800">
        <thead>
          <tr className="bg-slate-100">
            <th className={`${th} w-[36px] text-center`}>STT</th>
            <th className={`${th} text-left w-[110px]`}>Mã hàng</th>
            <th className={`${th} text-left`}>Tên hàng</th>
            <th className={`${th} text-center w-[50px]`}>ĐVT</th>
            <th className={`${th} text-left w-[120px]`}>Kho</th>
            <th className={`${th} text-right w-[80px]`}>Tồn đầu kỳ</th>
            <th className={`${th} text-right w-[80px]`}>Nhập</th>
            <th className={`${th} text-right w-[80px]`}>Xuất</th>
            <th className={`${th} text-right w-[80px]`}>Tồn cuối kỳ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className={`${td} text-center`}>{i + 1}</td>
              <td className={`${td} font-mono`}>{r.itemCode || "—"}</td>
              <td className={td}>{r.itemName || "—"}{r.lotNumber && r.lotNumber !== "DEFAULT" ? ` (Lô ${r.lotNumber})` : ""}</td>
              <td className={`${td} text-center`}>{r.unit || ""}</td>
              <td className={td}>{r.warehouseName || ""}</td>
              <td className={`${td} text-right`}>{formatNumber(r.opening)}</td>
              <td className={`${td} text-right`}>{formatNumber(r.inQty)}</td>
              <td className={`${td} text-right`}>{formatNumber(r.outQty)}</td>
              <td className={`${td} text-right font-semibold`}>{formatNumber(r.closing)}</td>
            </tr>
          ))}
          <tr className="bg-slate-50 font-bold">
            <td className={`${td} text-center`} colSpan={5}>Cộng ({rows.length} dòng)</td>
            <td className={`${td} text-right`}>{formatNumber(total.opening)}</td>
            <td className={`${td} text-right`}>{formatNumber(total.inQty)}</td>
            <td className={`${td} text-right`}>{formatNumber(total.outQty)}</td>
            <td className={`${td} text-right`}>{formatNumber(total.closing)}</td>
          </tr>
        </tbody>
      </table>
      <Signatures />
    </div>
  );
}

export interface StockCardPrintRow {
  date: string;
  documentNo: string;
  notes: string;
  warehouseName?: string;
  inQty: number | null;
  outQty: number | null;
  balance: number;
}

/** Mẫu in / PDF Sổ kho (thẻ kho) của một mặt hàng */
export function StockCardPrintTemplate({
  warehouseLabel,
  itemCode,
  itemName,
  unit,
  startDate,
  endDate,
  openingBalance,
  rows,
}: {
  warehouseLabel: string;
  itemCode: string;
  itemName: string;
  unit: string;
  startDate?: string;
  endDate?: string;
  openingBalance: number;
  rows: StockCardPrintRow[];
}) {
  const totalIn = rows.reduce((s, r) => s + (r.inQty || 0), 0);
  const totalOut = rows.reduce((s, r) => s + (r.outQty || 0), 0);
  const closing = rows.length ? rows[rows.length - 1].balance : openingBalance;
  return (
    <div className="text-[12px] leading-snug text-slate-900 font-serif bg-white" data-testid="stock-card-print-template">
      <ReportHeader
        title="SỔ KHO (THẺ KHO)"
        subtitle={<>{warehouseLabel} — Từ ngày {vnDate(startDate)} đến ngày {vnDate(endDate)}</>}
      />
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div><span className="font-semibold">Mã hàng:</span> {itemCode}</div>
        <div><span className="font-semibold">Tên hàng:</span> {itemName}</div>
        <div><span className="font-semibold">Đơn vị tính:</span> {unit}</div>
      </div>
      <table className="w-full border-collapse border border-slate-800">
        <thead>
          <tr className="bg-slate-100">
            <th className={`${th} w-[36px] text-center`}>STT</th>
            <th className={`${th} w-[80px] text-center`}>Ngày</th>
            <th className={`${th} w-[130px] text-left`}>Số chứng từ</th>
            <th className={`${th} text-left`}>Diễn giải</th>
            <th className={`${th} w-[110px] text-left`}>Kho</th>
            <th className={`${th} w-[80px] text-right`}>Nhập</th>
            <th className={`${th} w-[80px] text-right`}>Xuất</th>
            <th className={`${th} w-[80px] text-right`}>Tồn</th>
          </tr>
        </thead>
        <tbody>
          <tr className="bg-slate-50">
            <td className={`${td} text-center`}>—</td>
            <td className={td} colSpan={4}><span className="font-semibold">Tồn đầu kỳ</span></td>
            <td className={td}></td>
            <td className={td}></td>
            <td className={`${td} text-right font-semibold`}>{formatNumber(openingBalance)}</td>
          </tr>
          {rows.map((r, i) => (
            <tr key={i}>
              <td className={`${td} text-center`}>{i + 1}</td>
              <td className={`${td} text-center`}>{r.date}</td>
              <td className={`${td} font-mono`}>{r.documentNo}</td>
              <td className={td}>{r.notes}</td>
              <td className={td}>{r.warehouseName || ""}</td>
              <td className={`${td} text-right`}>{r.inQty != null ? formatNumber(r.inQty) : ""}</td>
              <td className={`${td} text-right`}>{r.outQty != null ? formatNumber(r.outQty) : ""}</td>
              <td className={`${td} text-right`}>{formatNumber(r.balance)}</td>
            </tr>
          ))}
          <tr className="bg-slate-50 font-bold">
            <td className={`${td} text-center`} colSpan={5}>Cộng phát sinh / Tồn cuối kỳ</td>
            <td className={`${td} text-right`}>{formatNumber(totalIn)}</td>
            <td className={`${td} text-right`}>{formatNumber(totalOut)}</td>
            <td className={`${td} text-right`}>{formatNumber(closing)}</td>
          </tr>
        </tbody>
      </table>
      <Signatures />
    </div>
  );
}
